import {
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
} from '@mobile-mapping/providers';
import { RightPanelTabName } from '@mobile-mapping/providers';
import type {
  ActionMenuItem,
  ControlMenuItemType,
} from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { useConnectProject } from '@trimble/connect-project-providers';
import { trnToUUID } from '@trimble/utilities';
import {
  AlignmentMeasurementCapability,
  AngleMeasurementCapability,
  LineMeasurementCapability,
  LineMeasurementConstraint,
  PointMeasurementCapability,
} from '@trimble/web3d-capabilities';
import { MeasurementsCapabilityProvider } from '@trimble/web3d-providers';
import { Accessor, batch, createEffect, createMemo, on } from 'solid-js';
import {
  MainToolbarMeasurementCapability,
  PointMeasurementOption,
} from './main-toolbar-measurements.types';

export interface UseMainToolbarMeasurementsOptions {
  readonly selectedRightPanelTab: Accessor<RightPanelTabName | undefined>;
  setSelectedRightPanelTab(tab: RightPanelTabName | undefined): void;
}

export interface UseMainToolbarMeasurementsResult {
  readonly measurementMenuItems: Accessor<ControlMenuItemType[]>;
  readonly isAnyMeasurementActive: Accessor<boolean>;
  deactivateAllMeasurements(): void;
}

/**
 * @intent Owns main-toolbar measurement dropdown state: capability wiring,
 * sub-option menus, and MeasurementDetails right-panel sync.
 */
export const useMainToolbarMeasurements = (
  options: UseMainToolbarMeasurementsOptions
): UseMainToolbarMeasurementsResult => {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const measurementsProvider = capabilityRegistry.get(
    MeasurementsCapabilityProvider
  );
  const { activeItems } = useDataContext();
  const { selectedTenantTrn } = useConnectProject();
  const { alignmentApi } = useApiServices();
  const railsQuery = alignmentApi.listRailsQuery(selectedTenantTrn);

  // Measurements are app-wide singletons in mobile-mapping (see
  // CapabilityRegistryProvider), so driving them from the global toolbar
  // affects every pane consistently. PolylineMeasurementCapability is not
  // registered in mobile-mapping yet, so it is intentionally omitted here.
  const measurementCapabilities = createMemo(
    (): MainToolbarMeasurementCapability[] => {
      const point = capabilityRegistry.get(PointMeasurementCapability)();
      const line = capabilityRegistry.get(LineMeasurementCapability)();
      const angle = capabilityRegistry.get(AngleMeasurementCapability)();
      const alignment = capabilityRegistry.get(
        AlignmentMeasurementCapability
      )();
      const entries: MainToolbarMeasurementCapability[] = [];

      if (point) {
        entries.push({
          id: 'point-measure',
          label: t('web3D.toolbar.actions.pointMeasurement'),
          icon: 'tc-icon-fixed-point',
          start: () => void point.startMeasurement(),
          isActive: () => point.isActive(),
          stop: () => void point.stopMeasurement(),
        });
      }
      if (line) {
        entries.push({
          id: 'line-measure',
          label: t('web3D.toolbar.actions.lineMeasurement'),
          icon: 'tc-icon-line',
          start: () => void line.startMeasurement(),
          isActive: () => line.isActive(),
          stop: () => void line.stopMeasurement(),
        });
      }
      if (angle) {
        entries.push({
          id: 'angle-measure',
          label: t('web3D.toolbar.actions.angleMeasurement'),
          icon: 'tc-icon-measure-angle',
          start: () => void angle.startMeasurement(),
          isActive: () => angle.isActive(),
          stop: () => void angle.stopMeasurement(),
        });
      }
      if (alignment) {
        entries.push({
          id: 'alignment-measure',
          label: t('web3D.toolbar.actions.alignmentMeasurement'),
          icon: 'tc-icon-path-navigation',
          start: () => void alignment.startMeasurement(),
          isActive: () => alignment.isActive(),
          isDisabled: () => !alignment.isEnabled(),
          stop: () => void alignment.stopMeasurement(),
        });
      }
      return entries;
    }
  );

  const isAnyMeasurementActive = createMemo((): boolean =>
    measurementCapabilities().some((m) => m.isActive())
  );

  createEffect(
    on(
      [
        () => measurementsProvider()?.selectedMeasurement(),
        isAnyMeasurementActive,
      ],
      ([selectedMeasurement, isMeasurementActive]) => {
        if (selectedMeasurement) {
          options.setSelectedRightPanelTab(
            RightPanelTabName.MeasurementDetails
          );
        } else if (
          options.selectedRightPanelTab() ===
            RightPanelTabName.MeasurementDetails &&
          !isMeasurementActive
        ) {
          options.setSelectedRightPanelTab(undefined);
        }
      }
    )
  );

  const deactivateAllMeasurements = (): void =>
    batch(() => measurementCapabilities().forEach((m) => m.stop()));

  const activateMeasurement = (measurementId: string): void => {
    batch(() => {
      const capabilities = measurementCapabilities();
      const target = capabilities.find((m) => m.id === measurementId);
      if (!target) {
        return;
      }

      if (target.isActive()) {
        target.stop();
        return;
      }

      capabilities.forEach((m) => {
        if (m.id !== measurementId) {
          m.stop();
        }
      });
      target.start();
    });
  };

  const getPointMeasurementOption = (): string => {
    const settings = measurementsProvider()?.pointMeasurementSettings();
    if (settings?.showXYCoordinates && settings.showZCoordinate) {
      return PointMeasurementOption.ThreeD;
    }
    if (settings?.showXYCoordinates) {
      return PointMeasurementOption.XYCoordinates;
    }
    if (settings?.showZCoordinate) {
      return PointMeasurementOption.ZCoordinate;
    }
    return '';
  };

  const setPointMeasurementOption = (value: string): void => {
    const pointCapability = capabilityRegistry.get(
      PointMeasurementCapability
    )();

    if (value === '') {
      pointCapability?.stopMeasurement();
      return;
    }

    batch(() => {
      if (!pointCapability?.isActive()) {
        pointCapability?.startMeasurement();
      }
      measurementsProvider()?.setPointMeasurementSettings({
        showXYCoordinates:
          value === PointMeasurementOption.ThreeD ||
          value === PointMeasurementOption.XYCoordinates,
        showZCoordinate:
          value === PointMeasurementOption.ThreeD ||
          value === PointMeasurementOption.ZCoordinate,
      });
    });
  };

  const isAlignmentRailOptionsEnabled = (): boolean => {
    const rails = railsQuery.data;
    const activeAlignmentId = activeItems.alignmentId;
    if (!rails || !activeAlignmentId) return false;

    const activeAlignmentUuid = trnToUUID(activeAlignmentId);
    return rails.some((rail) => rail.id === activeAlignmentUuid);
  };

  const isLineMeasurementConstraint = (
    value: string
  ): value is LineMeasurementConstraint =>
    value === LineMeasurementConstraint.Free ||
    value === LineMeasurementConstraint.ConstrainZ;

  const setLineMeasurementConstraint = (value: string): void => {
    const lineCapability = capabilityRegistry.get(LineMeasurementCapability)();
    if (!lineCapability || !isLineMeasurementConstraint(value)) {
      return;
    }

    batch(() => {
      lineCapability.setConstraint(value);
      if (!lineCapability.isActive()) {
        measurementCapabilities().forEach((capability) => {
          if (capability.id !== 'line-measure') {
            capability.stop();
          }
        });
        void lineCapability.startMeasurement();
      }
    });
  };

  const lineMeasurementOptionItems = (): ControlMenuItemType[] => {
    const line = capabilityRegistry.get(LineMeasurementCapability)();
    if (!line) {
      return [];
    }

    return [
      {
        id: 'line-measurement-options',
        type: 'radiogroup' as const,
        value: line.constraint,
        setValue: setLineMeasurementConstraint,
        isUnselectionAllowed: false,
        options: [
          {
            id: LineMeasurementConstraint.Free,
            label: t('measurements.line.free'),
          },
          {
            id: LineMeasurementConstraint.ConstrainZ,
            label: t('measurements.line.constrainZ'),
          },
        ],
      },
    ];
  };

  const pointMeasurementOptionItems = (): ControlMenuItemType[] => [
    {
      id: 'point-measurement-options',
      type: 'radiogroup' as const,
      value: getPointMeasurementOption,
      setValue: setPointMeasurementOption,
      isUnselectionAllowed: true,
      options: [
        {
          id: PointMeasurementOption.ThreeD,
          label: t('measurements.point.3D'),
        },
        {
          id: PointMeasurementOption.XYCoordinates,
          label: t('measurements.point.2DXY'),
        },
        {
          id: PointMeasurementOption.ZCoordinate,
          label: t('measurements.point.elevation'),
        },
      ],
    },
  ];

  const alignmentMeasurementOptionItems = (): ControlMenuItemType[] => {
    const alignment = capabilityRegistry.get(AlignmentMeasurementCapability)();
    if (!alignment) return [];

    const disabled = !isAlignmentRailOptionsEnabled();
    const tooltip = disabled
      ? t('web3D.toolbar.actions.alignmentMeasurementOptionsUnsupported')
      : undefined;

    return [
      {
        id: 'use-cant',
        type: 'checkbox' as const,
        label: t('web3D.toolbar.actions.alignmentMeasurementUseCant'),
        value: alignment.useCant,
        setValue: alignment.setUseCant,
        disabled,
        tooltip,
      },
      {
        id: 'use-gradient',
        type: 'checkbox' as const,
        label: t('web3D.toolbar.actions.alignmentMeasurementUseGradient'),
        value: alignment.useGradient,
        setValue: alignment.setUseGradient,
        disabled,
        tooltip,
      },
    ];
  };

  const measurementMenuItems = createMemo((): ControlMenuItemType[] => {
    const capabilities = measurementCapabilities();
    return capabilities.map((m) => {
      const item: ActionMenuItem = {
        id: m.id,
        type: 'action' as const,
        label: m.label,
        icon: m.icon,
        disabled: m.isDisabled?.() ?? false,
        selected: m.isActive(),
        onClick: () => activateMeasurement(m.id),
      };
      if (m.id === 'point-measure') {
        return {
          ...item,
          children: pointMeasurementOptionItems(),
        };
      }

      if (m.id === 'line-measure') {
        return {
          ...item,
          children: lineMeasurementOptionItems(),
        };
      }

      if (m.id === 'alignment-measure') {
        return {
          ...item,
          children: alignmentMeasurementOptionItems(),
        };
      }

      return item;
    });
  });

  return {
    measurementMenuItems,
    isAnyMeasurementActive,
    deactivateAllMeasurements,
  };
};
